import { useState } from 'react'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Search, Package, Plus, Grid, Square } from 'lucide-react'

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
  gst_rate?: number
}

interface ProductGridProps {
  products: Product[]
  onAddToCart: (product: Product) => void
  formatCurrency: (amount: number) => string
  onQuickAdd?: () => void
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  totalProducts?: number
  onSearch?: (searchTerm: string) => void
  searching?: boolean
}

export const ProductGrid = ({
  products,
  onAddToCart,
  formatCurrency,
  onQuickAdd,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  totalProducts = 0,
  onSearch,
  searching = false
}: ProductGridProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [gridColumns, setGridColumns] = useState<1 | 2>(1) // Default to 1 column for better UX

  // Use products as is (already filtered by backend)
  const filteredProducts = products

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setShowSearchDropdown(value.length > 0)

    // Call onSearch if provided (server-side search)
    if (onSearch) {
      onSearch(value)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Search Header */}
      <div className="flex-shrink-0 mb-4">
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search products by name or SKU..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowSearchDropdown(searchTerm.length > 0)}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                className="pl-10"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                </div>
              )}
              
              {/* Autocomplete Dropdown */}
              {showSearchDropdown && searchTerm && (
                <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredProducts.slice(0, 8).map(product => (
                    <div
                      key={product.id}
                      onClick={() => {
                        onAddToCart(product)
                        setSearchTerm('')
                        setShowSearchDropdown(false)
                      }}
                      className="p-3 hover:bg-orange-50 dark:hover:bg-orange-900/20 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{product.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {product.sku} • {product.category_name}
                            {typeof product.gst_rate === 'number' && (
                              <> • GST {product.gst_rate}%</>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-orange-600 dark:text-orange-400">
                            {formatCurrency(parseFloat(product.price?.toString() || '0'))}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Stock: {product.stock_quantity}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredProducts.length === 0 && (
                    <div className="p-3 text-sm text-gray-500 dark:text-gray-400 text-center">No products found</div>
                  )}
                </div>
              )}
            </div>

            {/* Add Custom Button */}
            {onQuickAdd && (
              <Button
                onClick={onQuickAdd}
                size="sm"
                className="flex items-center gap-1 h-10 px-3 text-xs bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
              >
                <Plus className="h-4 w-4" />
                Add Custom
              </Button>
            )}

            {/* Grid Layout Selector - now positioned at the end */}
            <div className="hidden lg:flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <Button
                variant={gridColumns === 1 ? "default" : "ghost"}
                size="sm"
                onClick={() => setGridColumns(1)}
                className="h-8 px-3"
                title="1 column layout"
              >
                <Square className="h-4 w-4" />
              </Button>
              <Button
                variant={gridColumns === 2 ? "default" : "ghost"}
                size="sm"
                onClick={() => setGridColumns(2)}
                className="h-8 px-3"
                title="2 column layout"
              >
                <Grid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Scrollable Products Grid */}
      <div className="flex-1 overflow-auto">
        {filteredProducts.length > 0 ? (
          <div className={`grid gap-4 pb-6 ${
            // Mobile: always 1 column (100% width)
            // Large screens: user choice between 1 or 2 columns
            gridColumns === 1 
              ? 'grid-cols-1' 
              : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {filteredProducts.map(product => {
              const price = parseFloat(product.price?.toString() || '0')
              const isLowStock = product.stock_quantity <= 10
              
              if (gridColumns === 1) {
                // Compact horizontal layout for 1-column
                return (
                  <Card key={product.id} className="overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer" onClick={() => onAddToCart(product)}>
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left: Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate" title={product.name}>
                                {product.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {product.category_name}
                                </span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {product.sku}
                                </span>
                                {typeof product.gst_rate === 'number' && (
                                  <>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      GST {product.gst_rate}%
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Center: Price & Stock */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                              {formatCurrency(price)}
                            </div>
                            <div className="flex items-center gap-1 justify-end">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Stock:</span>
                              <Badge 
                                variant={isLowStock ? 'warning' : 'secondary'}
                                size="sm"
                                className="text-xs px-1 py-0"
                              >
                                {product.stock_quantity}
                              </Badge>
                            </div>
                          </div>

                          {/* Right: Add Button */}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation()
                              onAddToCart(product)
                            }}
                            disabled={product.stock_quantity <= 0}
                            className="h-10 px-4 text-sm font-medium"
                            size="sm"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                )
              } else {
                // Original card layout for 2-column
                return (
                  <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer" onClick={() => onAddToCart(product)}>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate" title={product.name}>
                            {product.name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {product.category_name}
                          </p>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                              {formatCurrency(price)}
                            </span>
                            {typeof product.gst_rate === 'number' && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                GST {product.gst_rate}% (incl.)
                              </span>
                            )}
                          </div>
                          <Badge 
                            variant={isLowStock ? 'warning' : 'secondary'}
                            size="sm"
                            className="text-xs"
                          >
                            {product.stock_quantity}
                          </Badge>
                        </div>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            onAddToCart(product)
                          }}
                          disabled={product.stock_quantity <= 0}
                          className="w-full h-9 text-sm font-medium"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              }
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">No products found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm ? 'Try adjusting your search terms' : 'No products available'}
              </p>
            </div>
          </div>
        )}

        {/* Load More Button */}
        {!searchTerm && hasMore && onLoadMore && (
          <div className="flex flex-col items-center justify-center py-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Showing {products.length} of {totalProducts} products
            </p>
            <Button
              onClick={onLoadMore}
              disabled={loadingMore}
              variant="outline"
              className="min-w-[200px]"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Load More Products
                </>
              )}
            </Button>
          </div>
        )}

        {/* All loaded message */}
        {!searchTerm && !hasMore && products.length > 0 && totalProducts > 50 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              All {products.length} products loaded
            </p>
          </div>
        )}
      </div>
    </div>
  )
}